import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { CheckCircle2, Clock, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/hooks/useTranslation'
import { meteringStore, type MinutePackage } from './meteringStore'
import { module2Store } from './module2Store'
import { formatDay, subscriptionStore } from './subscriptionStore'

interface MinutesOfferModalProps {
  isOpen: boolean
  moduleName?: string
  onDismiss: () => void
}

/** A package's price in its own currency, written the way the reader's language writes it. */
export const formatPrice = (amount: number, currency: string, language: string): string => {
  try {
    return new Intl.NumberFormat(language, { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100)
  } catch {
    return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`
  }
}

export const formatMinutes = (seconds: number): number => Math.round(seconds / 60)

/**
 * Shown to a B2C subscriber whose minutes for the selected module are used up, and to
 * anyone who presses the minutes total in the sidebar. Dismissible, because coach chat
 * and reviews do not spend minutes.
 *
 * The body is the organisation's top-ups when there are any and it can take a payment,
 * with when the plan next brings minutes. After a return from Stripe it reports what
 * became of the payment instead.
 */
const MinutesOfferModal: React.FC<MinutesOfferModalProps> = ({ isOpen, moduleName, onDismiss }) => {
  const { t, currentLanguage } = useTranslation()

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onDismiss])

  if (!isOpen) return null

  const { offer, offerLoading, offerError, buyingPackageId, purchaseState, purchaseOrganisationName } = meteringStore
  const packages: MinutePackage[] = offer?.purchasable ? offer.packages : []
  const canBuy = packages.length > 0
  const confirming = purchaseState === 'confirming'
  // Opened from the sidebar with minutes left, rather than because they ran out.
  const topUp = !confirming && purchaseState === 'idle' && !module2Store.activeModuleIsOutOfMinutes
  const mine = subscriptionStore.mine
  const periodEnd = formatDay(mine?.subscription?.currentPeriodEnd, currentLanguage)
  const renewal =
    periodEnd && mine?.plan
      ? mine.subscription?.cancelAtPeriodEnd
        ? t('metering.offer.ends', { date: periodEnd })
        : t('metering.offer.renews', { date: periodEnd, count: formatMinutes(mine.plan.includedSeconds) })
      : null

  // One line for every refusal the server can send back from a checkout attempt.
  const errorMessage = offerError
    ? {
        PACKAGE_UNAVAILABLE: t('metering.offer.errors.packageUnavailable'),
        PAYMENTS_UNAVAILABLE: t('metering.offer.errors.paymentsUnavailable'),
        TOO_MANY_CHECKOUTS: t('metering.offer.errors.tooManyCheckouts'),
        CHECKOUT_BLOCKED: t('metering.offer.errors.checkoutBlocked'),
        SUBSCRIPTION_REQUIRED: t('metering.offer.errors.subscriptionRequired'),
      }[offerError] || t('metering.offer.errors.checkoutFailed')
    : null

  const title = confirming
    ? t('metering.offer.confirming.title')
    : purchaseState === 'added'
      ? t('metering.offer.added.title')
      : purchaseState === 'stillPending'
        ? t('metering.offer.stillPending.title')
        : purchaseState === 'error'
          ? t('metering.offer.failed.title')
          : topUp
            ? t('metering.offer.buyTitle')
            : t('metering.offer.title')

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss()
      }}
    >
      <div className="fixed inset-0 bg-black/40 pointer-events-none" />

      <div role="dialog" aria-modal="true" aria-labelledby="minutes-offer-title" className="relative w-full max-w-md rounded-2xl bg-white shadow-xl animate-in zoom-in-95 duration-150 font-[Inter]">
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 p-1.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          aria-label={t('metering.offer.dismiss')}
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-7 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#E8F5EE] text-[#0F4C3A]">
            {confirming ? <Loader2 className="h-6 w-6 animate-spin" /> : purchaseState === 'added' ? <CheckCircle2 className="h-6 w-6" /> : <Clock className="h-6 w-6" />}
          </div>

          <h2 id="minutes-offer-title" className="text-lg font-bold tracking-[-0.2px] text-[#0F4C3A]">
            {title}
          </h2>

          {/* Back from Stripe: what became of the payment. */}
          {confirming && <p className="mt-2 text-sm text-[#4A5D52]">{t('metering.offer.confirming.body')}</p>}
          {purchaseState === 'added' && (
            <p className="mt-2 text-sm text-[#4A5D52]">
              {purchaseOrganisationName ? t('metering.offer.added.otherOrganisation', { organisation: purchaseOrganisationName }) : t('metering.offer.added.body')}
            </p>
          )}
          {purchaseState === 'stillPending' && <p className="mt-2 text-sm text-[#4A5D52]">{t('metering.offer.stillPending.body')}</p>}
          {purchaseState === 'error' && <p className="mt-2 text-sm text-[#B42318]">{t('metering.offer.failed.body')}</p>}

          {/* The offer itself. */}
          {purchaseState === 'idle' && (
            <>
              <p className="mt-2 text-sm text-[#4A5D52]">{topUp ? t('metering.offer.buyBody') : moduleName ? t('metering.offer.bodyModule', { module: moduleName }) : t('metering.offer.body')}</p>
              <p className="mt-2 text-sm text-[#6B7D72]">{t('metering.offer.stillFree')}</p>
              {renewal && <p className="mt-2 text-sm text-[#6B7D72]">{renewal}</p>}

              {offerLoading && !offer && (
                <div className="mt-5 flex justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-[#0F4C3A]" />
                </div>
              )}

              {canBuy && (
                <div className="mt-5 flex flex-col gap-2 text-left">
                  {packages.map((pack) => (
                    <button
                      key={pack.id}
                      type="button"
                      disabled={buyingPackageId !== null}
                      onClick={() => meteringStore.buy(pack.id)}
                      className="flex items-center justify-between gap-3 rounded-xl border border-[#E1EAE5] px-4 py-3 text-left transition-colors hover:border-[#0F4C3A] hover:bg-[#F6FBF8] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-[#0F4C3A]">{pack.name}</span>
                        <span className="block text-xs text-[#6B7D72]">{t('metering.offer.minutes', { count: formatMinutes(pack.seconds) })}</span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2 text-sm font-semibold text-[#0F4C3A]">
                        {buyingPackageId === pack.id && <Loader2 className="h-4 w-4 animate-spin" />}
                        {formatPrice(pack.priceAmount, pack.currency, currentLanguage)}
                      </span>
                    </button>
                  ))}
                  <p className="mt-1 text-center text-xs text-[#6B7D72]">{t('metering.offer.validOneYear')}</p>
                </div>
              )}

              {errorMessage && <p className="mt-4 text-sm text-[#B42318]">{errorMessage}</p>}
            </>
          )}

          {!confirming && (
            <Button onClick={onDismiss} className="mt-6 w-full font-semibold" style={{ background: '#0F4C3A', color: '#fff' }}>
              {canBuy && purchaseState === 'idle' ? t('metering.offer.notNow') : t('metering.offer.dismiss')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default observer(MinutesOfferModal)
